import React, { useState } from 'react';
import moment from 'moment';
import { Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getTimeFormat } from '../../../../../common/getDateAndTimeFormat';
import { SharedMobileTimePicker } from '../../../../../common/ui/SharedMobileTimePicker';
import { workingHoursForDate } from '../../../../agendaView/utils/workingHours';
import { DATE_FORMAT } from '../../../../calendarWrapper/CalendarWrapper';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';
import { getNowRoundedDownSeconds } from '../../orderForm/orderFormDefaults';
import { dayPartOf, DayPart, groupSlotsByDayPart, startTimeSlots } from './startTimeSlots';

const SLOT_COLUMNS = 5;
const EARLIEST_PICKER_HOUR = 6;

type StartTimePickerProps = {
  date: string;
  timeSeconds: number;
  onChange: (seconds: number) => void;
};

export const StartTimePicker = ({ date, timeSeconds, onChange }: StartTimePickerProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  // @ts-ignore
  const { editOrderState } = useEditOrder();
  const [chosenPart, setChosenPart] = useState<DayPart | undefined>(undefined);

  const groups = groupSlotsByDayPart(startTimeSlots(workingHoursForDate(editOrderState.orderPage, account, date)));
  const valuePart = dayPartOf(timeSeconds);
  const wantedPart = chosenPart ?? valuePart;
  const activeGroup = groups.find((group) => group.part === wantedPart) ?? groups[0];

  const isClosestToNow = date === moment().format(DATE_FORMAT) && timeSeconds === getNowRoundedDownSeconds();
  const formatSlot = (seconds: number) => moment.unix(seconds).tz('utc').format(timeFormat);

  return (
    <Grid container flexDirection="column" gap={1}>
      <Grid container alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="body2" fontWeight={500}>
          {t('calendar.create_order.startTime')}
        </Typography>
        {isClosestToNow && (
          <Typography variant="caption" color="text.secondary">
            {t('calendar.create_order.closestToNow')}
          </Typography>
        )}
      </Grid>
      <Grid item maxWidth={120}>
        <SharedMobileTimePicker
          minTime={moment().set({ hour: EARLIEST_PICKER_HOUR, minute: 0, second: 0, millisecond: 0 })}
          value={moment.unix(timeSeconds).tz('utc')}
          minutesStep={15}
          onChange={(newValue) => {
            if (newValue) {
              onChange(newValue.utc().hours() * 3600 + newValue.utc().minutes() * 60);
            }
          }}
        />
      </Grid>
      {activeGroup && (
        <>
          <Tabs
            value={activeGroup.part}
            onChange={(_event, part: DayPart) => setChosenPart(part)}
            variant="scrollable"
            scrollButtons={false}>
            {groups.map((group) => (
              <Tab key={group.part} value={group.part} label={t(`calendar.create_order.dayParts.${group.part}`)} />
            ))}
          </Tabs>
          <Grid container columns={SLOT_COLUMNS} spacing={1}>
            {activeGroup.slots.map((slot) => (
              <Grid item xs={1} key={slot}>
                <Button
                  fullWidth
                  size="small"
                  variant={slot === timeSeconds ? 'contained' : 'outlined'}
                  onClick={() => onChange(slot)}
                  sx={{ minWidth: 0, paddingInline: 0.5 }}>
                  <span dir="ltr">{formatSlot(slot)}</span>
                </Button>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Grid>
  );
};
